/**
 * Cambios de nombre, correo y teléfono de un comprador: nunca se aplican directo desde
 * "Editar Comprador". Se levanta una solicitud con motivo + evidencia (RPC
 * `solicitar_cambio_datos_comprador`), que abre un ticket en "Soporte datos cliente", y la aprueba
 * quien tenga el permiso Aprobar en Autorizaciones de datos (`resolver_cambio_datos_comprador`).
 * Quien la pidió puede resolverla con justificación obligatoria: queda marcada `autoaprobada`.
 * La BD también bloquea el UPDATE directo de esos datos de un comprador (20261024080000).
 * Al aprobarla la BD aplica el cambio, incluida la cuenta de acceso si cambia el correo.
 *
 * DDL: Ejecuciones_manuales/20260923_autorizacion_cambio_datos_comprador.md
 */
import { supabase } from "@/integrations/supabase/client";
import { uploadTicketFile, type AdjuntoTipo, type PendingAdjunto } from "@/lib/portal-tickets/tickets-adjuntos";

// Tablas y RPC nuevas: aún no están en los tipos generados.
const sb = supabase as any;

export const VISTA_AUTORIZACIONES_DATOS = "/admin/autorizaciones-datos-comprador";
export const PIPELINE_SOPORTE_DATOS = "Soporte datos cliente";

export const CAMPOS_CON_AUTORIZACION = ["nombre_legal", "email", "telefono", "clave_pais_telefono"] as const;
export type CampoConAutorizacion = (typeof CAMPOS_CON_AUTORIZACION)[number];

export const ETIQUETA_CAMPO: Record<CampoConAutorizacion, string> = {
  nombre_legal: "Nombre",
  email: "Correo",
  telefono: "Teléfono",
  clave_pais_telefono: "Lada",
};

export type CambiosDatos = Partial<Record<CampoConAutorizacion, { actual: string | null; nuevo: string | null }>>;

/**
 * Desde 20261009 una solicitud también nace de un documento subido (lo extraído que
 * cambia un dato ya registrado) o de la app del cliente (p. ej. el correo). Puede traer
 * cualquier campo de este catálogo, espejo de `cambio_datos__etiquetas()` en la BD.
 */
export const ETIQUETA_CAMPO_SOLICITUD: Record<string, string> = {
  ...ETIQUETA_CAMPO,
  curp: "CURP",
  fecha_nacimiento: "Fecha de nacimiento",
  sexo: "Sexo",
  rfc: "RFC",
  regimen: "Régimen fiscal",
  nombre_comercial: "Nombre comercial",
  actividad_economica: "Actividad económica",
  direccion_fiscal_calle: "Calle (domicilio fiscal)",
  direccion_fiscal_num_ext: "Núm. exterior (domicilio fiscal)",
  direccion_fiscal_num_int: "Núm. interior (domicilio fiscal)",
  direccion_fiscal_colonia: "Colonia (domicilio fiscal)",
  direccion_fiscal_codigo_postal: "C.P. (domicilio fiscal)",
};

export function etiquetaCampo(campo: string): string {
  return ETIQUETA_CAMPO_SOLICITUD[campo] ?? campo;
}

/**
 * Nombre y domicilio fiscal: lo extraído de CSF, acta o CURP llega en MAYÚSCULAS y sin acentos.
 * Espejo de `cambio_datos__clave` / `cambio_datos__formato` en la BD
 * (Ejecuciones_manuales/documentos/06_DB_cambio_datos_homologar_mayusculas.md).
 */
const CAMPOS_TEXTO_LIBRE = new Set([
  "nombre_legal",
  "direccion_fiscal_calle",
  "direccion_fiscal_colonia",
  "direccion_fiscal_num_ext",
  "direccion_fiscal_num_int",
]);
const PARTICULAS = new Set(["de", "del", "la", "las", "los", "y", "e", "van", "von", "da", "di"]);

const colapsar = (v: string | null | undefined) => (v ?? "").trim().replace(/\s+/g, " ");

/** Llave para comparar: en nombre y domicilio ignora mayúsculas, acentos y espacios dobles. */
export function claveDato(campo: string, valor: string | null | undefined): string {
  const v = colapsar(valor);
  if (!CAMPOS_TEXTO_LIBRE.has(campo)) return v;
  return v.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/** `true` si el "nuevo" es el mismo dato que el "actual" (solo cambia mayúsculas o acentos). */
export function esMismoDato(campo: string, actual: string | null | undefined, nuevo: string | null | undefined): boolean {
  return claveDato(campo, actual) === claveDato(campo, nuevo);
}

/**
 * Cómo queda guardado: nombre y domicilio que vienen todo en MAYÚSCULAS pasan a tipo título. La
 * razón social de una persona moral (`esMoral`) se deja tal cual.
 */
export function formatoDato(campo: string, valor: string | null | undefined, esMoral = false): string {
  const v = colapsar(valor);
  if (!CAMPOS_TEXTO_LIBRE.has(campo) || (esMoral && campo === "nombre_legal") || !v || v !== v.toUpperCase()) return v;
  return v
    .split(" ")
    .map((w, i) => {
      const min = w.toLowerCase();
      if (i > 0 && PARTICULAS.has(min)) return min;
      if (/^[IVX]{1,4}$/.test(w)) return w;
      return min.charAt(0).toUpperCase() + min.slice(1);
    })
    .join(" ");
}

export type OrigenSolicitud = "edicion" | "documento" | "app";

export const ORIGEN_SOLICITUD: Record<OrigenSolicitud, string> = {
  edicion: "Editar Comprador",
  documento: "Documento subido",
  app: "App del cliente",
};

/** Cambios de una solicitud guardada: cualquier campo del catálogo. */
export type CambiosSolicitud = Record<string, { actual: string | null; nuevo: string | null }>;

/** Misma normalización que aplica PersonForm al guardar y el RPC al comparar. */
export function normalizarCampo(campo: CampoConAutorizacion, valor: unknown): string | null {
  const v = valor == null ? "" : String(valor).trim();
  if (campo === "email") return v ? v.toLowerCase() : null;
  if (campo === "clave_pais_telefono") return v || "MX";
  return v || null;
}

/** Campos protegidos cuyo valor en `payload` difiere del de `original`. */
export function detectarCambios(original: Record<string, any> | null | undefined, payload: Record<string, any>): CambiosDatos {
  const cambios: CambiosDatos = {};
  for (const campo of CAMPOS_CON_AUTORIZACION) {
    if (!(campo in payload)) continue;
    const actual = normalizarCampo(campo, original?.[campo]);
    const nuevo = normalizarCampo(campo, payload[campo]);
    if (actual !== nuevo) cambios[campo] = { actual: original?.[campo] ?? null, nuevo };
  }
  return cambios;
}

export function hayCambios(cambios: CambiosDatos): boolean {
  return Object.keys(cambios).length > 0;
}

/** El payload sin los campos protegidos: lo que sí se guarda directo. */
export function sinCamposConAutorizacion<T extends Record<string, any>>(payload: T): T {
  const copia: Record<string, any> = { ...payload };
  for (const campo of CAMPOS_CON_AUTORIZACION) delete copia[campo];
  return copia as T;
}

/** El valor vigente en BD (no el que trae la pantalla, que puede venir incompleto). */
export async function leerDatosVigentes(idPersona: number): Promise<Record<CampoConAutorizacion, string | null>> {
  const { data, error } = await supabase
    .from("personas")
    .select("nombre_legal, email, telefono, clave_pais_telefono")
    .eq("id", idPersona)
    .single();
  if (error) throw new Error(`No se pudieron leer los datos del comprador: ${error.message}`);
  return data as Record<CampoConAutorizacion, string | null>;
}

/** El UPDATE lo rechazó la guardia de la BD (comprador sin solicitud de autorización). */
export function esErrorGuardiaComprador(error: { code?: string; message?: string | null } | null | undefined): boolean {
  return error?.code === "42501" && !!error.message?.includes("solicitud de autorización");
}

export interface EvidenciaSubida {
  url: string;
  tipo: AdjuntoTipo;
  nombre: string;
  mime: string | null;
  tamano_bytes: number;
}

/** Sube las evidencias (mismo bucket y convención que los adjuntos de tickets). */
export async function subirEvidencias(pendientes: PendingAdjunto[]): Promise<EvidenciaSubida[]> {
  const lote = `cambio-datos/${crypto.randomUUID()}`;
  const subidas: EvidenciaSubida[] = [];
  for (const p of pendientes) {
    const up = await uploadTicketFile(lote, p.file);
    // uploadTicketFile ya mostró el motivo; sin todas las evidencias no se manda la solicitud.
    if (!up) throw new Error(`No se pudo subir la evidencia "${p.nombre}"`);
    subidas.push({ url: up.url, tipo: up.tipo, nombre: up.nombre, mime: up.mime, tamano_bytes: up.tamano });
  }
  return subidas;
}

export interface ResultadoSolicitud {
  id: number;
  id_ticket: number;
  numero_ticket: number;
  cambios: CambiosDatos;
  id_categoria?: number | null;
  /** Usuarios de la categoría "Aprobador" del pipeline (ligados + de sus roles): los propietarios del ticket. */
  notificar_emails?: string[];
  /** Versión anterior del RPC (asignado fijo). */
  asignado_email?: string;
}

/** A quién avisar del ticket nuevo, con la versión actual o la anterior del RPC. */
export function correosANotificar(r: ResultadoSolicitud): string[] {
  if (r.notificar_emails) return r.notificar_emails;
  return r.asignado_email ? [r.asignado_email] : [];
}

export async function solicitarCambioDatos(params: {
  idPersona: number;
  cambios: CambiosDatos;
  motivo: string;
  evidencias: EvidenciaSubida[];
  vista: string;
}): Promise<ResultadoSolicitud> {
  const nuevos = Object.fromEntries(Object.entries(params.cambios).map(([k, v]) => [k, v?.nuevo ?? null]));
  const { data, error } = await sb.rpc("solicitar_cambio_datos_comprador", {
    p_id_persona: params.idPersona,
    p_cambios: nuevos,
    p_motivo: params.motivo.trim(),
    p_evidencias: params.evidencias,
    p_vista: params.vista,
  });
  if (error) throw new Error(error.message);
  return data as ResultadoSolicitud;
}

export type EstatusSolicitud = "pendiente" | "aprobada" | "rechazada";

export interface SolicitudCambioDatos {
  id: number;
  id_persona: number;
  cambios: CambiosSolicitud;
  /** Ausente antes de 20261009: se trata como "edicion". */
  origen?: OrigenSolicitud | null;
  id_documento?: number | null;
  motivo: string;
  estatus: EstatusSolicitud;
  plan_correo: { escenario?: string; motivo?: string | null } | null;
  id_ticket: number | null;
  vista_origen: string | null;
  id_usuario_solicitante: string;
  email_solicitante: string;
  rol_solicitante: string | null;
  fecha_solicitud: string;
  id_usuario_revisor: string | null;
  email_revisor: string | null;
  rol_revisor: string | null;
  fecha_revision: string | null;
  comentario_revision: string | null;
  /** La aprobó quien la pidió (con justificación en comentario_revision). Ausente antes de 20261024030000. */
  autoaprobada?: boolean | null;
}

/** Mínimo de la justificación al resolver la solicitud propia (igual que el RPC). */
export const MIN_JUSTIFICACION_PROPIA = 10;

export interface EvidenciaSolicitud {
  id: number;
  id_solicitud: number;
  url: string;
  nombre: string | null;
  mime: string | null;
  tipo: AdjuntoTipo;
  fecha_creacion: string;
}

export interface BitacoraSolicitud {
  id: number;
  id_solicitud: number;
  accion: "solicitada" | "aprobada" | "rechazada" | "autoaprobada";
  email_usuario: string | null;
  rol_usuario: string | null;
  fecha: string;
  detalle: Record<string, any>;
}

export interface SolicitudDetallada extends SolicitudCambioDatos {
  comprador: string;
  /** Persona moral: su nombre_legal es la razón social y no se pasa a tipo título. */
  es_moral: boolean;
  numero_ticket: number | null;
  evidencias: EvidenciaSolicitud[];
  bitacora: BitacoraSolicitud[];
}

function agrupar<T extends { id_solicitud: number }>(filas: T[]): Map<number, T[]> {
  const m = new Map<number, T[]>();
  for (const f of filas) m.set(f.id_solicitud, [...(m.get(f.id_solicitud) ?? []), f]);
  return m;
}

export type FiltroEstatusSolicitud = EstatusSolicitud | "todas";

export interface PaginaSolicitudes {
  filas: SolicitudDetallada[];
  total: number;
}

/** Une personas, tickets, evidencias y bitácora a una lista de solicitudes ya leída. */
async function detallarSolicitudes(lista: SolicitudCambioDatos[]): Promise<SolicitudDetallada[]> {
  if (!lista.length) return [];
  const ids = lista.map((s) => s.id);
  const idsPersona = [...new Set(lista.map((s) => s.id_persona))];
  const idsTicket = [...new Set(lista.map((s) => s.id_ticket).filter((x): x is number => x != null))];

  const [personas, tickets, evidencias, bitacora] = await Promise.all([
    supabase.from("personas").select("id, nombre_legal, tipo_persona").in("id", idsPersona),
    idsTicket.length
      ? sb.from("tickets").select("id, numero").in("id", idsTicket)
      : Promise.resolve({ data: [] as { id: number; numero: number }[] }),
    sb.from("solicitudes_cambio_datos_evidencias").select("*").in("id_solicitud", ids).order("id"),
    sb.from("solicitudes_cambio_datos_bitacora").select("*").in("id_solicitud", ids).order("fecha"),
  ]);

  const nombrePorId = new Map<number, string>((personas.data ?? []).map((p: any) => [p.id, p.nombre_legal as string]));
  const morales = new Set<number>((personas.data ?? []).filter((p: any) => p.tipo_persona === "pm").map((p: any) => p.id));
  const numeroPorTicket = new Map<number, number>((tickets.data ?? []).map((t: any) => [t.id, t.numero as number]));
  const evidPorSol = agrupar<EvidenciaSolicitud>(evidencias.data ?? []);
  const bitPorSol = agrupar<BitacoraSolicitud>(bitacora.data ?? []);

  return lista.map((s) => ({
    ...s,
    comprador: nombrePorId.get(s.id_persona) ?? `Persona ${s.id_persona}`,
    es_moral: morales.has(s.id_persona),
    numero_ticket: s.id_ticket != null ? numeroPorTicket.get(s.id_ticket) ?? null : null,
    evidencias: evidPorSol.get(s.id) ?? [],
    bitacora: bitPorSol.get(s.id) ?? [],
  }));
}

/**
 * Una página de solicitudes (`FILAS_POR_PAGINA` por petición) con el total del filtro.
 * Orden por fecha de solicitud. null = la tabla aún no existe (DDL pendiente).
 */
export async function listarSolicitudesPagina(params: {
  estatus: FiltroEstatusSolicitud;
  page: number;
  ascendente?: boolean;
  porPagina?: number;
}): Promise<PaginaSolicitudes | null> {
  const porPagina = params.porPagina ?? 15;
  const desde = (Math.max(1, params.page) - 1) * porPagina;
  let q = sb
    .from("solicitudes_cambio_datos_comprador")
    .select("*", { count: "exact" })
    .order("fecha_solicitud", { ascending: !!params.ascendente })
    .order("id", { ascending: !!params.ascendente })
    .range(desde, desde + porPagina - 1);
  if (params.estatus !== "todas") q = q.eq("estatus", params.estatus);
  const { data, error, count } = await q;
  if (error) {
    if (error.code === "42P01" || error.code === "PGRST205") return null;
    throw new Error(error.message);
  }
  const filas = await detallarSolicitudes((data ?? []) as SolicitudCambioDatos[]);
  return { filas, total: count ?? filas.length };
}

export type ConteoSolicitudes = Record<FiltroEstatusSolicitud, number>;

/** Total por pestaña, del servidor (head + count exact; la tabla es chica). */
export async function contarSolicitudesPorEstatus(): Promise<ConteoSolicitudes> {
  const contar = async (estatus?: EstatusSolicitud) => {
    let q = sb.from("solicitudes_cambio_datos_comprador").select("id", { count: "exact", head: true });
    if (estatus) q = q.eq("estatus", estatus);
    const { count, error } = await q;
    if (error) {
      if (error.code === "42P01" || error.code === "PGRST205") return 0;
      throw new Error(error.message);
    }
    return count ?? 0;
  };
  const [todas, pendiente, aprobada, rechazada] = await Promise.all([
    contar(),
    contar("pendiente"),
    contar("aprobada"),
    contar("rechazada"),
  ]);
  return { todas, pendiente, aprobada, rechazada };
}

export interface FilaCambio {
  campo: string;
  actual: string | null;
  nuevo: string | null;
  /** Solo cambian mayúsculas, acentos o espacios: no es un cambio real. */
  sinCambioReal: boolean;
}

/** Cambios de una solicitud en orden de llegada, marcando los que no cambian el dato. */
export function filasDeCambios(cambios: CambiosSolicitud | null | undefined): FilaCambio[] {
  return Object.entries(cambios ?? {}).map(([campo, v]) => ({
    campo,
    actual: v?.actual ?? null,
    nuevo: v?.nuevo ?? null,
    sinCambioReal: esMismoDato(campo, v?.actual, v?.nuevo),
  }));
}

/** Etiquetas de los datos que cambian de verdad (vacío = la solicitud no cambia nada real). */
export function etiquetasCambiosReales(cambios: CambiosSolicitud | null | undefined): string[] {
  return filasDeCambios(cambios)
    .filter((f) => !f.sinCambioReal)
    .map((f) => etiquetaCampo(f.campo));
}

export async function resolverSolicitud(idSolicitud: number, aprobar: boolean, comentario: string) {
  const { data, error } = await sb.rpc("resolver_cambio_datos_comprador", {
    p_id_solicitud: idSolicitud,
    p_aprobar: aprobar,
    p_comentario: comentario.trim() || null,
  });
  if (error) throw new Error(error.message);
  return data as {
    id: number;
    estatus: EstatusSolicitud;
    id_ticket: number | null;
    email_solicitante: string;
    autoaprobada?: boolean;
  };
}

/** El usuario cerró el diálogo de solicitud: la pantalla no debe mostrar error. */
export class CambioDatosCancelado extends Error {
  constructor() {
    super("Solicitud de cambio cancelada");
    this.name = "CambioDatosCancelado";
  }
}

export function esCancelacionCambioDatos(error: unknown): boolean {
  return error instanceof CambioDatosCancelado;
}
